import { api } from 'src/api';

// Direct API calls, not a store action: an action's run() cancels its own call in flight, and one screen can show
// several badges at once (the header and the Subscription tab, or a page of Pro Pricing rows).
const NO_OPTIONS = {
  cancelToken: {}
};

/**
 * Long enough for every badge on a screen, and the next screen, to share one read; short, because property-service
 * already keeps the tiers five minutes, and an answer kept here as well would show a plan change later still.
 */
const KEEP_MS = 60 * 1000;

/** A page of Pro Pricing rows names up to 50 companies; this many reads go out at once, the rest wait their turn. */
const MAX_IN_FLIGHT = 4;
const kept = new Map();
const waiting = [];
let inFlight = 0;
const takeTurn = () => {
  if (inFlight < MAX_IN_FLIGHT) {
    inFlight += 1;
    return Promise.resolve();
  }
  return new Promise(resolve => waiting.push(resolve));
};

// A finished read hands its turn straight to the next one waiting, so a new read can't slip in between and go over.
const endTurn = () => {
  const next = waiting.shift();
  if (next) next();else inFlight -= 1;
};
const fetchTier = async companyId => {
  await takeTurn();
  try {
    const resp = await api.Companies.getSubscriptionTier({
      companyId
    }, NO_OPTIONS);
    const plan = resp === null || resp === void 0 ? void 0 : resp.response;
    // `allows` is what the plan includes, for the locks still to come (LB-6187, LB-6233); the badge reads only the tier.
    return typeof (plan === null || plan === void 0 ? void 0 : plan.tier) === 'string' && plan.tier ? {
      tier: plan.tier,
      allows: plan.allows || {}
    } : null;
  } catch (_unused) {
    // 403 (another company), 404 (billing options off), 503 (stripe-service down): the badge only explains the plan,
    // and the server enforces it either way, so nothing shows.
    return null;
  } finally {
    endTurn();
  }
};

/**
 * The company's plan (LB-6228), `{ tier, allows }`, or null when it can't be read. One read serves every badge for the
 * company, and it's kept a minute; a failed read isn't kept, so the next badge asks again.
 */
export const readSubscriptionTier = companyId => {
  const now = Date.now();
  const entry = kept.get(companyId);
  if (entry && now - entry.at < KEEP_MS) return entry.read;
  const read = fetchTier(companyId).then(plan => {
    var _kept$get;
    if (!plan && ((_kept$get = kept.get(companyId)) === null || _kept$get === void 0 ? void 0 : _kept$get.read) === read) kept.delete(companyId);
    return plan;
  });
  kept.set(companyId, {
    read,
    at: now
  });
  return read;
};

/** Forget every kept answer: on logout, so the next session in this tab reads with its own token. */
export const forgetSubscriptionTiers = () => kept.clear();